import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Box } from 'core/components';
import UtilizationChart from 'app/components/cloudpakFlowIndicators/UtilizationChart';
import CloudPakFlowMetricsWrapper from 'app/components/cloudpakFlowIndicators/CloudPakFlowMetricsWrapper';
import { canSafelyDivideFlowRates } from '@kentik/ui-shared/util/sampleRate';

@inject('$auth', '$devices')
@observer
export default class UniversalCloudPakCard extends Component {
  state = {
    selectedTimePeriod: '1_day' // Default to 1 day
  };

  handleTimePeriodChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      this.setState({ selectedTimePeriod: selectedOption.value });
    }
  };

  // Find the flow data model for a given metric name
  getModel(flowDataModel, metricName) {
    if (!flowDataModel || !flowDataModel.models) {
      return null;
    }
    return (
      flowDataModel.models.find((item) => {
        const metricsArr = item.get('metrics');
        return metricsArr[0] === metricName;
      }) || null
    );
  }

  // Extract the timeseries array from the flow data model
  getModelDataArray(flowDataModel, metricName = 'flows_discovered') {
    const model = this.getModel(flowDataModel, metricName);
    return model ? model.get('timeseries') : null;
  }

  // Extract a P95 rollup value from the flow data model
  getModelRollup(flowDataModel, metricName, rollupKey) {
    const model = this.getModel(flowDataModel, metricName);
    const value = model ? model.get(rollupKey) : null;
    return Number.isFinite(value) ? value : null;
  }

  getDataResolution(receivedFlowValue, storedFlowValue) {
    // Determine data resolution based on the ratio
    let dataResolution = null;

    if (canSafelyDivideFlowRates(receivedFlowValue, storedFlowValue)) {
      const ratio = receivedFlowValue / storedFlowValue;
      if (ratio <= 1) {
        dataResolution = 'ok';
      } else if (ratio < 2) {
        dataResolution = 'downsampled';
      } else {
        dataResolution = 'low';
      }
    }
    return dataResolution;
  }

  render() {
    const { pak_fps, id: plan_id, description, onAlertDataChange, $devices } = this.props;
    // note that pak_fps is the "max FPS" for Universal CloudPaks
    const { selectedTimePeriod } = this.state;

    const deviceCount = ['aws', 'gce', 'azure', 'oci'].reduce(
      (total, provider) => total + $devices.getCloudDeviceCount(provider, plan_id),
      0
    );

    // Create a render function that has access to the current scope
    const renderUtilizationChart = ({ loading, rawFlowModel, receivedFlowModel, storedFlowModel, error }) => {
      const rawFlowArray = this.getModelDataArray(rawFlowModel, 'in');
      const receivedFlowArray = this.getModelDataArray(receivedFlowModel, 'in');
      const storedFlowArray = this.getModelDataArray(storedFlowModel, 'out');

      // Summary numbers use P95 rollups (matching the FlowPak card)
      const rawFlowValue = this.getModelRollup(rawFlowModel, 'in', 'p95_in');
      const receivedFlowValue = this.getModelRollup(receivedFlowModel, 'in', 'p95_in');
      const storedFlowValue = this.getModelRollup(storedFlowModel, 'out', 'p95_out');

      const hasValidFlow = canSafelyDivideFlowRates(rawFlowValue, receivedFlowValue);

      const avgConfiguredSamplingRate = hasValidFlow ? `1 : ${(rawFlowValue / receivedFlowValue).toFixed(1)}` : null;

      const dataResolution = this.getDataResolution(receivedFlowValue, storedFlowValue);

      // Pass alert data to parent component
      if (onAlertDataChange) {
        onAlertDataChange({
          avgConfiguredSamplingRate,
          dataResolution,
          receivedFps: receivedFlowValue ?? null,
          maxFps: pak_fps ?? null
        });
      }

      return (
        <UtilizationChart
          rawFlowArray={rawFlowArray}
          receivedFlowArray={receivedFlowArray}
          storedFlowArray={storedFlowArray}
          rawFlowValue={rawFlowValue}
          receivedFlowValue={receivedFlowValue}
          storedFlowValue={storedFlowValue}
          planMaxFps={pak_fps}
          description={description}
          deviceCount={deviceCount}
          avgConfiguredSamplingRate={avgConfiguredSamplingRate}
          dataResolution={dataResolution}
          loading={loading}
          error={error}
          pak_fps={pak_fps}
          selectedTimePeriod={selectedTimePeriod}
          onTimePeriodChange={this.handleTimePeriodChange}
        />
      );
    };

    return (
      <Box width="100%">
        <CloudPakFlowMetricsWrapper lookback={selectedTimePeriod} plan_id={plan_id}>
          {renderUtilizationChart}
        </CloudPakFlowMetricsWrapper>
      </Box>
    );
  }
}
